/**
 * The Arcane Spire's held hum (TOWERS_V2): while any spire holds a beam, a drone plays under the battle and follows the
 * strongest beam's ramp — a fifth higher and much brighter at full charge than on lock-on — so the player hears the
 * spire winding up. It is synthesised live (a sample loop could not glide in pitch and tone without seams): five
 * partials of A3 through a low-pass whose cutoff opens with the ramp, a 2 Hz throb on top, and a band of filtered
 * noise for the crackle. Every parameter glides (setTargetAtTime), so a frame-by-frame caller never clicks. The node
 * graph is built on first use and torn down after `IdleSeconds` of silence.
 */

/** The drone's partials (× the base frequency) and their levels. */
export const HUM_PARTIALS: readonly (readonly [number, number])[] = [[1, 1], [1.5, 0.45], [2, 0.4], [3, 0.2], [4, 0.1]];
/** The hum's shape against the ramp (0–1): base frequency, low-pass cutoff (Hz) and level. Pure (unit-tested). */
export function humAt(ramp: number): { freq: number; cutoff: number; level: number } {
  const r = Math.max(0, Math.min(1, ramp));
  return { freq: 220 * Math.pow(1.5, r), cutoff: 500 + 3500 * r * r, level: 0.05 + 0.07 * r };
}
export const HUM = {
  /** Glide time constant of every parameter (s). */
  glide: 0.08,
  /** Seconds of silence after which the oscillators are stopped. */
  idleSeconds: 1.5,
  /** The throb: rate (Hz) and depth (share of the level). */
  throbHz: 2, throbDepth: 0.3,
  /** The smallest change in ramp or level (0..1) that re-aims the glides; below it the running glide stands. */
  retargetEps: 0.01,
} as const;

export class HumLoop {
  private oscs: OscillatorNode[] = [];
  private noise: AudioBufferSourceNode | null = null;
  private filter: BiquadFilterNode | null = null;
  private out: GainNode | null = null;
  private lfo: OscillatorNode | null = null;
  private silentFor = 0;
  private lastAt = 0;

  constructor(readonly ctx: AudioContext, private readonly bus: AudioNode) {}

  /** Sets the hum for this frame: `level` 0..1 (0 fades it out), `ramp` 0..1. */
  set(level: number, ramp: number): void {
    const now = this.ctx.currentTime;
    const dt = Math.max(0, now - this.lastAt);
    this.lastAt = now;
    if (level <= 0) {
      if (!this.out) return;
      if (this.sentLevel !== 0) { this.out.gain.setTargetAtTime(0, now, HUM.glide); this.sentLevel = 0; }
      if ((this.silentFor += dt) > HUM.idleSeconds) this.stop();
      return;
    }
    this.silentFor = 0;
    if (!this.out) { this.build(); this.sentRamp = -1; this.sentLevel = -1; }
    // Called every frame: re-aim the glides only when the target moved. Seven setTargetAtTime a frame for as long as
    // a spire held its beam piled thousands of automation events onto the params (M30 #11, per-frame audio work).
    const lv = Math.min(1, level);
    if (Math.abs(ramp - this.sentRamp) < HUM.retargetEps && Math.abs(lv - this.sentLevel) < HUM.retargetEps) return;
    this.sentRamp = ramp; this.sentLevel = lv;
    const h = humAt(ramp);
    this.oscs.forEach((o, i) => o.frequency.setTargetAtTime(h.freq * HUM_PARTIALS[i][0], now, HUM.glide));
    this.filter!.frequency.setTargetAtTime(h.cutoff, now, HUM.glide);
    this.out!.gain.setTargetAtTime(h.level * lv, now, HUM.glide);
  }
  private sentRamp = -1;
  private sentLevel = -1;

  private build(): void {
    const ctx = this.ctx, now = ctx.currentTime;
    this.out = ctx.createGain();
    this.out.gain.value = 0;
    const throb = ctx.createGain();
    throb.gain.value = 1 - HUM.throbDepth / 2;
    this.lfo = ctx.createOscillator();
    this.lfo.frequency.value = HUM.throbHz;
    const depth = ctx.createGain();
    depth.gain.value = HUM.throbDepth / 2;
    this.lfo.connect(depth).connect(throb.gain);
    this.filter = ctx.createBiquadFilter();
    this.filter.type = "lowpass";
    this.filter.Q.value = 2;
    this.filter.frequency.value = humAt(0).cutoff;
    this.filter.connect(throb).connect(this.out).connect(this.bus);
    for (const [k, g] of HUM_PARTIALS) {
      const o = ctx.createOscillator();
      o.type = k === 1 ? "triangle" : "sine";
      o.frequency.value = humAt(0).freq * k;
      const pg = ctx.createGain();
      pg.gain.value = g * 0.5;
      o.connect(pg).connect(this.filter);
      o.start(now);
      this.oscs.push(o);
    }
    // the crackle: a second of noise, looped, band-passed high
    const buf = ctx.createBuffer(1, ctx.sampleRate, ctx.sampleRate);
    const d = buf.getChannelData(0);
    for (let i = 0; i < d.length; i++) d[i] = Math.random() < 0.004 ? (Math.random() * 2 - 1) : (Math.random() * 2 - 1) * 0.05;
    this.noise = ctx.createBufferSource();
    this.noise.buffer = buf;
    this.noise.loop = true;
    const band = ctx.createBiquadFilter();
    band.type = "bandpass"; band.frequency.value = 3000; band.Q.value = 0.8;
    const ng = ctx.createGain(); ng.gain.value = 0.6;
    this.noise.connect(band).connect(ng).connect(this.out);
    this.noise.start(now);
    this.lfo.start(now);
  }

  /** Stops and drops the graph (silence, a muted game, a level left). */
  stop(): void {
    for (const o of this.oscs) { try { o.stop(); } catch { /* not started */ } o.disconnect(); }
    this.oscs = [];
    try { this.noise?.stop(); this.lfo?.stop(); } catch { /* not started */ }
    this.noise?.disconnect(); this.lfo?.disconnect(); this.out?.disconnect();
    this.noise = null; this.lfo = null; this.out = null; this.filter = null; this.silentFor = 0;
  }
}
